import { localize, siteContent, type Locale, type LocalizedText } from "@/content/site";

export type ProofBarItem = {
  code: string;
  label: string;
  value: string;
};

export type TrustPillarItem = {
  title: LocalizedText;
  desc: LocalizedText;
};

export type MethodologyStep = {
  title: LocalizedText;
  desc: LocalizedText;
};

export type WorkPattern = {
  slug: string;
  title: LocalizedText;
  businessSituation: LocalizedText;
  workflowProblem: LocalizedText;
  systemPattern: LocalizedText;
  dataRequired: readonly LocalizedText[];
  productionControls: readonly LocalizedText[];
  metricsToBaseline: readonly LocalizedText[];
  firstPhase: LocalizedText;
  links: readonly {
    href: string;
    label: LocalizedText;
  }[];
};

export const proofTrustCopy = {
  work: {
    heroLabel: {
      en: "Real work, anonymized",
      fr: "Mandats réels, anonymisés",
    },
    heroTitle: {
      en: "Four engagements. The operating problem, intervention, controls, and evidence boundary.",
      fr: "Quatre mandats. Le problème opérationnel, l’intervention, les contrôles et les limites de la preuve.",
    },
    heroSubtitle: {
      en: "These profiles describe real Vert AI engagements. Company identities, system details, numeric outcomes, and measurement periods are omitted where they are confidential or not approved for public use.",
      fr: "Ces profils décrivent de vrais mandats de Vert AI. L’identité des entreprises, certains détails des systèmes, les résultats chiffrés et les périodes de mesure sont omis lorsqu’ils sont confidentiels ou non approuvés pour publication.",
    },
    methodologyEyebrow: {
      en: "Methodology",
      fr: "Méthodologie",
    },
    methodologyTitle: {
      en: "How proof is created before scale.",
      fr: "Comment établir la preuve avant de passer à l’échelle.",
    },
    methodologyIntro: {
      en: "The sequence is intentionally narrow: pick the expensive workflow, make the baseline visible, confirm AI is the right mechanism, build the smallest production-grade system, and expand only after trust is earned.",
      fr: "La séquence est volontairement ciblée : choisir le processus coûteux, rendre la mesure de référence visible, confirmer que l’IA est le bon mécanisme, construire le plus petit système de production utile, puis élargir seulement après avoir gagné la confiance.",
    },
    patternsEyebrow: {
      en: "Engagement evidence",
      fr: "Preuves de mandat",
    },
    patternsTitle: {
      en: "What was changed—and what is not being claimed.",
      fr: "Ce qui a été modifié — et ce qui n’est pas affirmé.",
    },
    patternsIntro: {
      en: "Each profile identifies the operating context, baseline problem, intervention, data, production controls, and measurement frame. No outcome number, client identity, or measurement period is inferred when it is not in the approved public record.",
      fr: "Chaque profil précise le contexte opérationnel, le problème de départ, l’intervention, les données, les contrôles de production et le cadre de mesure. Aucun résultat chiffré, nom de client ni période de mesure n’est déduit en l’absence d’une preuve approuvée pour publication.",
    },
    trustEyebrow: {
      en: "Trust pillars",
      fr: "Piliers de confiance",
    },
    trustTitle: {
      en: "The controls a buyer should expect to inspect.",
      fr: "Les contrôles qu’un acheteur devrait pouvoir inspecter.",
    },
    trustIntro: {
      en: "Production trust is not a claim. It is the visible operating layer around the system: controls, ownership, review, monitoring, and measurement.",
      fr: "La confiance en production n’est pas une affirmation. C’est la couche opérationnelle visible autour du système : contrôles, responsabilité, validation, surveillance et mesure.",
    },
    founderEyebrow: {
      en: "Founder experience",
      fr: "Expérience du fondateur",
    },
    founderTitle: {
      en: "Founder-led production experience.",
      fr: "Expérience de production dirigée par le fondateur.",
    },
    founderNote: {
      en: "Louis Vuitton, Canada Goose, Lowe’s, and Home Hardware are references to Guillaume’s prior professional experience. They are not represented here as Vert AI clients.",
      fr: "Louis Vuitton, Canada Goose, Lowe’s et Home Hardware sont des références à l’expérience professionnelle antérieure de Guillaume. Ces entreprises ne sont pas présentées comme des clientes de Vert AI.",
    },
    surfacesEyebrow: {
      en: "Next buying step",
      fr: "Prochaine étape d’achat",
    },
    surfacesTitle: {
      en: "Use the proof system to choose the right conversation.",
      fr: "Utiliser le système de preuve pour choisir la bonne conversation.",
    },
    ctaHeadline: {
      en: "Bring one costly workflow or recurring decision.",
      fr: "Présentez un processus coûteux ou une décision récurrente.",
    },
    ctaDesc: {
      en: "A fit call should clarify whether the work is ready for a production AI system, a data foundation pass, a narrower workflow automation, or no build yet.",
      fr: "Un appel de cadrage devrait clarifier si le travail est prêt pour un système d’IA en production, un chantier de fondation de données, une automatisation de processus plus ciblée ou aucune mise en œuvre pour l’instant.",
    },
  },
} as const;

export const trustPillars = [
  {
    title: { en: "Evaluation", fr: "Évaluation" },
    desc: {
      en: "Expected behavior, edge cases, failure modes, and acceptance criteria are defined before launch.",
      fr: "Le comportement attendu, les cas limites, les modes d’échec et les critères d’acceptation sont définis avant le lancement.",
    },
  },
  {
    title: { en: "Observability", fr: "Observabilité" },
    desc: {
      en: "Usage, quality, latency, exceptions, drift, incidents, and business signals need visible monitoring.",
      fr: "L’usage, la qualité, la latence, les exceptions, la dérive, les incidents et les indicateurs d’affaires doivent faire l’objet d’une surveillance visible.",
    },
  },
  {
    title: { en: "Human review", fr: "Validation humaine" },
    desc: {
      en: "People stay in control where risk, judgment, customer impact, or commercial accountability matters.",
      fr: "Les humains restent en contrôle lorsque le risque, le jugement, l’incidence sur la clientèle ou l’imputabilité commerciale comptent.",
    },
  },
  {
    title: { en: "Governance", fr: "Gouvernance" },
    desc: {
      en: "Ownership, documentation, change control, and review cadence are part of the system design.",
      fr: "La responsabilité, la documentation, le contrôle des changements et la cadence d’examen font partie de la conception du système.",
    },
  },
  {
    title: { en: "Data quality", fr: "Qualité des données" },
    desc: {
      en: "Source definitions, transformations, freshness, ownership, permissions, and missing data are made inspectable.",
      fr: "Les définitions des sources, les transformations, la fraîcheur, la responsabilité, les autorisations et les données manquantes deviennent vérifiables.",
    },
  },
  {
    title: { en: "Security and privacy", fr: "Sécurité et confidentialité" },
    desc: {
      en: "Access, permissions, sensitive data handling, security review, and vendor exposure are scoped before automation expands.",
      fr: "L’accès, les autorisations, le traitement des données sensibles, l’examen de sécurité et l’exposition aux fournisseurs sont cadrés avant d’élargir l’automatisation.",
    },
  },
  {
    title: { en: "ROI measurement", fr: "Mesure du rendement" },
    desc: {
      en: "Baseline metrics, adoption, cycle time, quality, and operating impact are measured before scale.",
      fr: "Les mesures de référence, l’adoption, le temps de cycle, la qualité et l’impact opérationnel sont établis avant de passer à l’échelle.",
    },
  },
] as const satisfies readonly TrustPillarItem[];

export const methodologySteps = [
  {
    title: { en: "Select expensive workflow", fr: "Choisir un processus coûteux" },
    desc: {
      en: "Start where delay, manual effort, inconsistency, or decision risk already has operational cost.",
      fr: "Commencer là où les délais, l’effort manuel, l’incohérence ou le risque décisionnel ont déjà un coût opérationnel.",
    },
  },
  {
    title: { en: "Define baseline", fr: "Définir la mesure de référence" },
    desc: {
      en: "Document the current cycle time, exception rate, review effort, quality issues, adoption constraints, and business impact.",
      fr: "Documenter le temps de cycle actuel, le taux d’exception, l’effort de validation, les problèmes de qualité, les contraintes d’adoption et l’incidence opérationnelle.",
    },
  },
  {
    title: { en: "Expose data readiness", fr: "Établir la préparation des données" },
    desc: {
      en: "Map sources, ownership, definitions, quality gaps, permissions, and missing instrumentation.",
      fr: "Cartographier les sources, les responsabilités, les définitions, les écarts de qualité, les autorisations et l’instrumentation manquante.",
    },
  },
  {
    title: { en: "Design workflow/system controls", fr: "Concevoir les contrôles du processus et du système" },
    desc: {
      en: "Define system boundaries, escalation paths, human review, audit trails, and fallback behavior.",
      fr: "Définir les limites du système, les parcours de transfert, la validation humaine, les pistes d’audit et les comportements de repli.",
    },
  },
  {
    title: { en: "Evaluate with human review", fr: "Évaluer avec validation humaine" },
    desc: {
      en: "Test against real examples, edge cases, acceptance criteria, and review queues before release.",
      fr: "Tester avec des exemples réels, des cas limites, des critères d’acceptation et des files de validation avant le lancement.",
    },
  },
  {
    title: { en: "Deploy with observability", fr: "Déployer avec une observabilité complète" },
    desc: {
      en: "Launch with monitoring for usage, quality, exceptions, latency, ownership, and business metrics.",
      fr: "Lancer avec une surveillance de l’usage, de la qualité, des exceptions, de la latence, de la responsabilité et des indicateurs d’affaires.",
    },
  },
  {
    title: { en: "Expand only after trust is earned", fr: "Élargir seulement après avoir gagné la confiance" },
    desc: {
      en: "Scale after users trust the system, controls work, and the measured workflow improves.",
      fr: "Étendre seulement lorsque les utilisateurs font confiance au système, que les contrôles tiennent et que le processus mesuré s’améliore.",
    },
  },
] as const satisfies readonly MethodologyStep[];

export const workPatterns = [
  {
    slug: "spreadsheet-reporting-to-decision-intelligence",
    title: {
      en: "From spreadsheet-driven reporting to decision intelligence",
      fr: "Des rapports sur tableurs à l’intelligence décisionnelle",
    },
    businessSituation: {
      en: "Leadership depends on recurring spreadsheets, manual reconciliations, and meeting-by-meeting interpretation to decide where to act.",
      fr: "La direction dépend de tableurs récurrents, de rapprochements manuels et d’interprétations en réunion pour décider où agir.",
    },
    workflowProblem: {
      en: "The reporting workflow produces numbers, but the decision logic, exceptions, owners, and next actions are not encoded anywhere reliable.",
      fr: "Le processus de production de rapports produit des chiffres, mais la logique décisionnelle, les exceptions, les responsables et les prochaines actions ne sont pas formalisées de façon fiable.",
    },
    systemPattern: {
      en: "A governed decision layer that combines metric definitions, source-of-truth data, exception logic, and recommended actions for recurring reviews.",
      fr: "Une couche décisionnelle gouvernée qui combine les définitions de mesures, les données de référence, la logique d’exception et les actions recommandées pour les examens récurrents.",
    },
    dataRequired: [
      { en: "Reporting spreadsheets and source extracts", fr: "Tableurs de production de rapports et extractions des sources" },
      { en: "Metric definitions and business rules", fr: "Définitions de mesures et règles d’affaires" },
      { en: "Decision cadence, owners, and escalation paths", fr: "Cadence des décisions, responsables et parcours de transfert" },
    ],
    productionControls: [
      { en: "Metric dictionary and lineage checks", fr: "Dictionnaire de mesures et contrôles de lignage" },
      { en: "Exception thresholds and review workflow", fr: "Seuils d’exception et processus de validation" },
      { en: "Change log for definitions and rules", fr: "Journal des changements de définitions et règles" },
    ],
    metricsToBaseline: [
      { en: "Reporting cycle time", fr: "Temps de cycle de production des rapports" },
      { en: "Manual reconciliation effort", fr: "Effort de rapprochement manuel" },
      { en: "Decision latency after reporting", fr: "Latence décisionnelle après la production des rapports" },
    ],
    firstPhase: {
      en: "A source-of-truth model for one decision review, a metric dictionary, exception logic, and a working decision surface for the operating team.",
      fr: "Un modèle de référence pour un examen décisionnel, un dictionnaire de mesures, une logique d’exception et une interface décisionnelle utilisable par l’équipe opérationnelle.",
    },
    links: [
      { href: "/services/decision-intelligence", label: { en: "Explore decision intelligence systems", fr: "Explorer les systèmes d’intelligence décisionnelle" } },
      { href: "/services/data-platforms", label: { en: "Review AI-ready data foundations", fr: "Voir les fondations de données pour l’IA" } },
      { href: "/contact", label: { en: "Discuss a reporting-to-decision workflow", fr: "Discuter d’un processus reliant les rapports aux décisions" } },
    ],
  },
  {
    slug: "manual-support-triage-to-human-reviewed-ai-workflow",
    title: {
      en: "From manual support triage to a human-reviewed AI workflow",
      fr: "Du triage manuel des demandes à un processus IA avec validation humaine",
    },
    businessSituation: {
      en: "Support or operations teams spend significant time classifying requests, gathering context, drafting responses, and routing exceptions.",
      fr: "Les équipes de soutien ou des opérations consacrent beaucoup de temps à classer les demandes, à rassembler le contexte, à rédiger des réponses et à acheminer les exceptions.",
    },
    workflowProblem: {
      en: "Triage depends on individual judgment, queue discipline, and copy-paste work, which makes quality, speed, and escalation hard to manage.",
      fr: "Le triage dépend du jugement individuel, de la gestion de la file et du copier-coller, ce qui complique le contrôle de la qualité, de la rapidité et des transferts.",
    },
    systemPattern: {
      en: "An AI-assisted triage workflow that classifies requests, retrieves approved context, drafts next steps, and routes uncertain cases for human review.",
      fr: "Un processus de triage assisté par IA qui classe les demandes, récupère le contexte approuvé, propose les prochaines étapes et achemine les cas incertains vers une validation humaine.",
    },
    dataRequired: [
      { en: "Historical tickets, emails, chats, or intake records", fr: "Demandes, courriels, clavardages ou dossiers de réception historiques" },
      { en: "Approved knowledge base, policies, and escalation rules", fr: "Base de connaissances, politiques et règles de transfert approuvées" },
      { en: "Queue categories, priorities, outcomes, and review notes", fr: "Catégories de files, priorités, résultats et notes de validation" },
    ],
    productionControls: [
      { en: "Confidence thresholds and human review queues", fr: "Seuils de confiance et files de validation humaine" },
      { en: "Audit logs for classification and suggested actions", fr: "Journaux d’audit pour le classement et les actions proposées" },
      { en: "Approved-source retrieval and response boundaries", fr: "Recherche dans les sources approuvées et limites de réponse" },
    ],
    metricsToBaseline: [
      { en: "First-response and resolution time", fr: "Temps de première réponse et de résolution" },
      { en: "Escalation and rework rate", fr: "Taux de transfert et de reprise" },
      { en: "Reviewer effort per request", fr: "Effort de validation par demande" },
    ],
    firstPhase: {
      en: "A controlled triage assistant for one queue, with classification, retrieval, draft recommendations, human review, and quality reporting.",
      fr: "Un assistant de triage contrôlé pour une file, avec classement, recherche, recommandations provisoires, validation humaine et suivi de la qualité.",
    },
    links: [
      { href: "/services/production-ai-systems", label: { en: "Explore production AI and workflows", fr: "Explorer l’IA en production et les processus" } },
      { href: "/solutions/customer-support-service-systems", label: { en: "Review customer support system patterns", fr: "Voir les schémas de systèmes de service à la clientèle" } },
      { href: "/contact", label: { en: "Discuss a human-reviewed triage workflow", fr: "Discuter d’un processus de triage avec validation humaine" } },
    ],
  },
  {
    slug: "scattered-operational-data-to-ai-ready-data-foundation",
    title: {
      en: "From scattered operational data to an AI-ready data foundation",
      fr: "De données opérationnelles dispersées à une fondation prête pour l’IA",
    },
    businessSituation: {
      en: "Teams want AI or better analytics, but operational data lives across tools, exports, spreadsheets, and inconsistent definitions.",
      fr: "Les équipes veulent utiliser l’IA ou améliorer leurs analyses, mais les données opérationnelles sont réparties entre plusieurs outils, exportations et tableurs aux définitions incohérentes.",
    },
    workflowProblem: {
      en: "Models, dashboards, and automations cannot be trusted because the input data is fragmented, stale, ambiguous, or owned by no clear team.",
      fr: "Les modèles, les tableaux de bord et les automatisations ne sont pas fiables lorsque les données d’entrée sont fragmentées, périmées, ambiguës ou sans responsable clair.",
    },
    systemPattern: {
      en: "An AI-ready data foundation with source mapping, core entities, quality checks, semantic definitions, access controls, and activation paths.",
      fr: "Une fondation de données prête pour l’IA avec cartographie des sources, entités principales, contrôles de qualité, définitions sémantiques, contrôles d’accès et chemins d’activation.",
    },
    dataRequired: [
      { en: "Operational systems, exports, and reporting tables", fr: "Systèmes opérationnels, exportations et tables de production de rapports" },
      { en: "Entity definitions, identifiers, and ownership rules", fr: "Définitions d’entités, identifiants et règles de responsabilité" },
      { en: "Freshness, completeness, and quality expectations", fr: "Attentes de fraîcheur, d’exhaustivité et de qualité" },
    ],
    productionControls: [
      { en: "Data quality tests and freshness monitoring", fr: "Tests de qualité des données et surveillance de la fraîcheur" },
      { en: "Access rules for sensitive or customer data", fr: "Règles d’accès aux données sensibles ou aux données clients" },
      { en: "Documented semantic layer and ownership model", fr: "Couche sémantique documentée et modèle de responsabilité" },
    ],
    metricsToBaseline: [
      { en: "Data defect and reconciliation rate", fr: "Taux de défauts de données et de rapprochement" },
      { en: "Time to produce trusted datasets", fr: "Temps requis pour produire des jeux de données fiables" },
      { en: "Report/model inputs with clear ownership", fr: "Intrants des rapports et des modèles avec responsabilité claire" },
    ],
    firstPhase: {
      en: "A governed data model for one AI or decision workflow, with source mapping, quality checks, documentation, and activation-ready outputs.",
      fr: "Un modèle de données gouverné pour un processus d’IA ou décisionnel, avec cartographie des sources, contrôles de qualité, documentation et résultats prêts à l’activation.",
    },
    links: [
      { href: "/services/data-platforms", label: { en: "Explore Data Platforms for AI", fr: "Explorer les plateformes de données pour l’IA" } },
      { href: "/services/ai-verification-review", label: { en: "Verify one AI initiative before the next decision", fr: "Vérifier une initiative d’IA avant la prochaine décision" } },
      { href: "/contact", label: { en: "Discuss an AI-ready data foundation", fr: "Discuter d’une fondation de données prête pour l’IA" } },
    ],
  },
  {
    slug: "ai-prototype-to-monitored-production-workflow",
    title: {
      en: "From an AI prototype to a monitored production workflow",
      fr: "Du prototype IA au processus de production surveillé",
    },
    businessSituation: {
      en: "A prototype has shown promise, but it lacks the controls, integrations, evaluation, and ownership required for daily business use.",
      fr: "Un prototype est prometteur, mais il lui manque les contrôles, les intégrations, les évaluations et les responsabilités requises pour une utilisation opérationnelle quotidienne.",
    },
    workflowProblem: {
      en: "The prototype works in a demo path but breaks down around edge cases, review queues, data changes, user trust, and operating handoff.",
      fr: "Le prototype fonctionne en démonstration, mais se fragilise face aux cas limites, aux files de validation, aux changements de données, à la confiance des utilisateurs et au transfert opérationnel.",
    },
    systemPattern: {
      en: "A production workflow that wraps the AI capability with evaluation, monitoring, human review, integration, fallback behavior, and ownership.",
      fr: "Un processus de production qui encadre la capacité d’IA par l’évaluation, la surveillance, la validation humaine, l’intégration, les comportements de repli et une responsabilité claire.",
    },
    dataRequired: [
      { en: "Prototype prompts, model behavior, and test examples", fr: "Instructions du prototype, comportement du modèle et exemples de test" },
      { en: "Real workflow inputs, outputs, and exception cases", fr: "Intrants, résultats et cas d’exception du processus réel" },
      { en: "User roles, approval paths, and launch constraints", fr: "Rôles des utilisateurs, chemins d’approbation et contraintes de lancement" },
    ],
    productionControls: [
      { en: "Evaluation harness and launch acceptance criteria", fr: "Grille d’évaluation et critères d’acceptation au lancement" },
      { en: "Monitoring for quality, usage, latency, and failures", fr: "Surveillance de la qualité, de l’usage, de la latence et des défaillances" },
      { en: "Fallback logic, ownership, and support cadence", fr: "Logique de repli, responsabilité et cadence de soutien" },
    ],
    metricsToBaseline: [
      { en: "Prototype accuracy against real examples", fr: "Précision du prototype sur des exemples réels" },
      { en: "Human review effort and override rate", fr: "Effort de validation humaine et taux de modification manuelle" },
      { en: "Workflow adoption and production incidents", fr: "Adoption du processus et incidents de production" },
    ],
    firstPhase: {
      en: "A production-readiness rebuild for one workflow, with evaluation, integration plan, monitoring model, review logic, and launch criteria.",
      fr: "Une remise à niveau d’un processus pour la production, avec évaluation, plan d’intégration, modèle de surveillance, logique de validation et critères de lancement.",
    },
    links: [
      { href: "/services/production-ai-systems", label: { en: "Explore production AI systems", fr: "Explorer les systèmes d’IA en production" } },
      { href: "/insights/production-grade-ai", label: { en: "Read what production-grade AI requires", fr: "Lire ce qu’exige l’IA de production" } },
      { href: "/contact", label: { en: "Discuss moving a prototype to production", fr: "Discuter du passage d’un prototype en production" } },
    ],
  },
] as const satisfies readonly WorkPattern[];

export const workSurfaceLinks = [
  {
    href: "/services",
    title: { en: "Compare engagement paths", fr: "Comparer les types de mandats" },
    desc: {
      en: "Map the operating problem to production AI and workflows, readiness mapping, data foundations, or decision systems.",
      fr: "Relier le problème opérationnel à l’IA en production, à un diagnostic de préparation, à des fondations de données ou à un système décisionnel.",
    },
  },
  {
    href: "/industries",
    title: { en: "Explore industry patterns", fr: "Explorer les schémas par industrie" },
    desc: {
      en: "See the recurring workflows, data realities, and risks across retail and ecommerce, CPG, and franchise or multi-location operations.",
      fr: "Voir les processus récurrents, les réalités de données et les risques du commerce de détail, du commerce électronique, des biens de consommation et des réseaux multisites.",
    },
  },
  {
    href: "/contact",
    title: { en: "Discuss a costly workflow", fr: "Discuter d’un processus coûteux" },
    desc: {
      en: "Describe the workflow, data challenge, or prototype you need to turn into operating capability.",
      fr: "Décrire le processus, le défi de données ou le prototype à transformer en capacité opérationnelle.",
    },
  },
] as const;

export const getDefaultProofBarItems = (locale: Locale): ProofBarItem[] => [
  {
    code: "01",
    label: locale === "fr" ? "Fondateur" : "Founder-led",
    value: localize(siteContent.proof.founderLedDelivery, locale),
  },
  {
    code: "02",
    label: locale === "fr" ? "Expérience" : "Experience",
    value: localize(siteContent.proof.selectedOperatorExperience, locale),
  },
  {
    code: "03",
    label: locale === "fr" ? "Exécution" : "Execution",
    value:
      locale === "fr"
        ? "Stratégie, architecture, mise en œuvre, évaluation et transfert restent connectés du cadrage à la production."
        : "Strategy, architecture, implementation, evaluation, and handoff stay connected from scope to production.",
  },
  {
    code: "04",
    label: locale === "fr" ? "Expertise" : "Expertise",
    value:
      locale === "fr"
        ? "IA, données, automatisation de processus et systèmes décisionnels sont traités comme une seule capacité opérationnelle."
        : "AI, data, workflow automation, and decision-system design are treated as one operating capability.",
  },
  {
    code: "05",
    label: locale === "fr" ? "Marché" : "Market",
    value: localize(siteContent.brand.geography, locale),
  },
];

export const localizeProofText = (text: LocalizedText, locale: Locale) => localize(text, locale);
